import { useState } from "react";
import { api, ApiError } from "../../api/client";
import type {
  Discrepancy,
  TraceDocument,
  TraceCommitment,
} from "../../api/types";
import { certLabel } from "../../components/TraceStatusPanel";
import { S } from "../../styles";

/**
 * The certificate as read, beside what the order required.
 *
 * Side by side deliberately. The reconciliation already produced a verdict, but
 * an inspector clearing or rejecting a shipment is making an airworthiness call
 * and has to be able to see the two things being compared rather than trust a
 * conclusion. Where the machine could not read a field, that is shown as "not
 * read" rather than left blank — an empty cell reads as "not required".
 *
 * Each certificate carries its own findings. It used to show only its extracted
 * blocks, which meant a form whose part number was wrong for this order still
 * rendered a green "✓ Block 14 signed" and nothing else — the tick answers
 * "was it signed", and read as "this document is fine". An inspector working
 * down the pack one form at a time would clear it. The findings list above says
 * otherwise, but it is a different list in a different place, and being right
 * somewhere else on the screen is not the same as being right here.
 *
 * A document with no findings of its own is labelled as exactly that, not as
 * passed: a contradiction between two forms belongs to neither of them, and a
 * certificate can be individually impeccable and still be one of the two.
 */

function Row({
  label,
  value,
  flagged,
}: {
  label: string;
  value: React.ReactNode;
  flagged?: boolean;
}) {
  return (
    <>
      <dt style={{ ...S.muted, fontSize: 12 }}>{label}</dt>
      <dd
        style={{
          margin: 0,
          fontSize: 12,
          ...(flagged ? { color: "#a61e1e", fontWeight: 600 } : {}),
        }}
      >
        {value}
        {flagged && <span style={{ marginLeft: 6 }}>⛔</span>}
      </dd>
    </>
  );
}

/**
 * Which block a finding is about, so the value that failed can be marked in
 * place. The wrong part number is only obvious to someone who already knows
 * what was ordered — and the order is a different panel further up the page.
 */
const FIELD_BY_CODE: Record<string, string> = {
  pn_mismatch: "part_number",
  status_work_mismatch: "status_work",
  status_work_better_than_ordered: "status_work",
  quantity_uncovered: "quantity",
  quantity_over_covered: "quantity",
  serial_mismatch: "serial_number",
  serial_substituted: "serial_number",
  signature_date_missing: "signature_date",
  signature_date_after_receipt: "signature_date",
  issuing_org_differs_from_supplier: "issuing_organization",
  cert_type_unacceptable: "document_type",
};

function NotRead() {
  return <span style={{ ...S.muted, fontStyle: "italic" }}>not read</span>;
}

/** Blocks 13 and 14 stated in words, because the numbers mean nothing alone. */
export function ReleaseBlocks({ doc }: { doc: TraceDocument }) {
  const f = doc.extracted_fields;
  if (!f) return null;
  if (f.block_14_signed) {
    return (
      <span style={{ color: "#1f7a3d" }}>
        ✓ Block 14 signed — approved for return to service
      </span>
    );
  }
  if (f.block_13_signed) {
    return (
      <span style={{ color: "#a61e1e" }}>
        Block 13 only — conformity to design data, not a return to service
      </span>
    );
  }
  return (
    <span style={{ color: "#a61e1e" }}>
      Neither block 13 nor block 14 signed
    </span>
  );
}

export function CommitmentSummary({
  commitment,
}: {
  commitment?: TraceCommitment | null;
}) {
  const req = commitment?.requirement;
  if (!req || (req.acceptable_cert_types ?? []).length === 0) {
    return (
      <p style={{ ...S.muted, fontSize: 12 }}>
        This order predates traceability commitments — there is nothing recorded
        to check the certificate against. Inspect manually.
      </p>
    );
  }
  return (
    <dl
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: "4px 12px",
        margin: 0,
      }}
    >
      <Row
        label="Accepts"
        value={req.acceptable_cert_types.map(certLabel).join(", ")}
      />
      <Row
        label="Release"
        value={
          req.airworthiness_release_required
            ? "Airworthiness release required — a conformity statement is not accepted"
            : "Conformity or trace statement is sufficient"
        }
      />
      {req.back_to_birth_required && (
        <Row
          label="Life history"
          value="Complete back-to-birth record required"
        />
      )}
      {!req.part_class_known && (
        <Row
          label="Caveat"
          value="Part class could not be established — requirement derived from the condition code alone"
        />
      )}
    </dl>
  );
}

/**
 * This document's own verdict, stated where the document is read.
 *
 * `implicated` findings are rendered separately, in a quieter tone, and never
 * marked against a block value. A pack that contradicts itself implicates every
 * form in the group without the machine knowing which one is wrong — putting a
 * red cross on this form's quantity would assert something it has no basis for,
 * and the inspector would reject the wrong certificate.
 *
 * The tone is the point, not decoration. Rendering a contradiction in the same
 * red as a failed check made three cards look identically defective when one of
 * the three was a good form; the words said "nothing is wrong with this form"
 * and the colour said otherwise, and on a scan the colour wins.
 */
function DocumentVerdict({
  findings,
  implicated,
}: {
  findings: Discrepancy[];
  implicated: Discrepancy[];
}) {
  const blocking = findings.filter((f) => f.severity === "blocking");
  const advisory = findings.filter((f) => f.severity !== "blocking");

  if (findings.length === 0 && implicated.length === 0) {
    return (
      <p style={{ ...S.muted, fontSize: 12, margin: "6px 0 0" }}>
        No findings against this document on its own — check the pack-level
        findings above.
      </p>
    );
  }

  const tone = blocking.length
    ? { bg: "#fff5f5", border: "#ffc9c9", fg: "#a61e1e" }
    : { bg: "#fffaf0", border: "#ffe3bf", fg: "#8a5a1c" };

  return (
    <>
      {findings.length > 0 && (
        <div
          style={{
            background: tone.bg,
            border: `1px solid ${tone.border}`,
            borderRadius: 4,
            padding: "6px 8px",
            marginTop: 6,
            fontSize: 12,
            color: tone.fg,
          }}
        >
          {[...blocking, ...advisory].map((f, i) => (
            <div key={`${f.code}-${i}`} style={{ marginTop: i === 0 ? 0 : 4 }}>
              <strong>{f.severity === "blocking" ? "⛔" : "•"}</strong>{" "}
              {f.message}
            </div>
          ))}
        </div>
      )}

      {implicated.length > 0 && (
        <div
          style={{
            background: "#f4f6f8",
            border: "1px solid #dde3e8",
            borderRadius: 4,
            padding: "6px 8px",
            marginTop: 6,
            fontSize: 12,
            color: "#465561",
          }}
        >
          <div>
            {findings.length === 0
              ? "Nothing is wrong with this form on its own. It is one of several covering the same unit that disagree, and the pack cannot be reconciled until the ones that do not belong are set aside:"
              : "This form is also one of several covering the same unit that disagree:"}
          </div>
          {implicated.map((f, i) => (
            <div key={`${f.code}-${i}`} style={{ marginTop: 4 }}>
              ↔ {f.expected ? `Expected ${f.expected}` : f.code}
              {f.actual ? ` — the pack says ${f.actual}` : ""}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Setting a form aside, and the reason that makes it a record rather than a
 * click.
 *
 * The reason box is not optional and not a dropdown. The useful reasons are all
 * specific — "page 2 of the previous shipment's pack", "supplier resent rev B,
 * this is rev A" — and this text is the only account of a judgement the machine
 * could not make, so it is what a later reviewer reads.
 *
 * Three things here exist because the first version of this control was a dead
 * end, and it is worth naming them so they are not tidied away again.
 *
 * The confirm button is never disabled. It used to grey out until the reason
 * had text, which meant an inspector who clicked it got *silence* — no request,
 * no message, nothing to read — which is precisely the failure this whole
 * feature was built to remove. Refusing and saying why beats refusing quietly.
 *
 * The label is a label, not a placeholder. Placeholder text vanishes on focus
 * and reads as prose rather than as a prompt for input.
 *
 * The form closes only once the write has actually landed. Closing on click
 * discarded what the inspector had typed if the server refused it, and left the
 * failure explained somewhere else on the card.
 */
function ExclusionControl({
  doc,
  canExclude,
  busy,
  onChange,
}: {
  doc: TraceDocument;
  canExclude: boolean;
  busy?: boolean;
  onChange: (excluded: boolean, reason?: string) => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);

  if (doc.excluded_at) {
    return (
      <button
        type="button"
        style={S.btnSm}
        disabled={busy}
        onClick={() => void onChange(false)}
      >
        ↩ Return to pack
      </button>
    );
  }

  // A buyer sees the control's absence, not a button that 403s. Same rule the
  // server enforces: removing a form is what releases the shipment.
  if (!canExclude) return null;

  if (!open) {
    return (
      <button
        type="button"
        style={S.btnSm}
        disabled={busy}
        onClick={() => setOpen(true)}
      >
        Set aside…
      </button>
    );
  }

  const submit = () => {
    if (reason.trim() === "") {
      setErr(
        "Say why this certificate does not belong — it is the only record of the judgement.",
      );
      return;
    }
    setErr(null);
    void Promise.resolve(onChange(true, reason.trim()))
      .then(() => {
        setOpen(false);
        setReason("");
      })
      .catch(() => {
        // The parent shows the message. What matters here is that the box stays
        // open with the text still in it, so it can be corrected and resent.
      });
  };

  return (
    <form
      style={{ marginTop: 8 }}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <label
        htmlFor={`exclude-${doc.id}`}
        style={{ ...S.muted, fontSize: 12, display: "block", marginBottom: 4 }}
      >
        Why does this certificate not belong to this shipment?
      </label>
      <input
        id={`exclude-${doc.id}`}
        style={{ ...S.input, marginBottom: 6 }}
        autoFocus
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
          if (err) setErr(null);
        }}
      />
      {err && (
        <p style={{ fontSize: 12, color: "#a61e1e", margin: "0 0 6px" }}>
          {err}
        </p>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" style={S.btnSm} disabled={busy}>
          Set aside
        </button>
        <button
          type="button"
          style={S.btnSm}
          disabled={busy}
          onClick={() => {
            setOpen(false);
            setReason("");
            setErr(null);
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export function CertificatePanel({
  doc,
  findings,
  canExclude = false,
  busy = false,
  onExclusionChange,
}: {
  doc: TraceDocument;
  findings?: Discrepancy[] | null;
  canExclude?: boolean;
  busy?: boolean;
  onExclusionChange?: (
    documentId: string,
    excluded: boolean,
    reason?: string,
  ) => void | Promise<void>;
}) {
  const [openErr, setOpenErr] = useState<string | null>(null);
  const f = doc.extracted_fields;
  const excluded = doc.excluded_at != null;
  // An excluded form produced no findings, so any that still name it are stale
  // — the response that carried them predates the exclusion.
  const all = excluded ? [] : (findings ?? []);
  const mine = all.filter((d) => d.document_id === doc.id);
  // Only contradictions. Every pack-wide finding names its contributing forms,
  // but `issuing_org_differs_from_supplier` on six certificates from one MRO is
  // one fact about the pack, and repeating it under all six is the noise the
  // deduplication exists to remove. A contradiction is the one case where a form
  // reads as clean and is not.
  const implicated = all.filter(
    (d) =>
      d.code === "pack_inconsistent" &&
      d.document_id !== doc.id &&
      (d.document_ids ?? []).includes(doc.id),
  );
  const blockingCount = mine.filter((d) => d.severity === "blocking").length;
  const flagged = new Set(
    mine.map((d) => FIELD_BY_CODE[d.code]).filter(Boolean),
  );
  // Only this form's own failures. A form that merely contradicts another is
  // not itself defective, and bordering it red said that it was.
  const bordered = blockingCount > 0;

  return (
    <div
      style={{
        background: excluded ? "#f3f4f5" : "#f8fafb",
        // A form that fails a check is bordered, so the pack can be read by
        // scanning down it rather than by cross-referencing two lists.
        border: bordered ? "1px solid #ffc9c9" : "1px solid rgba(0,0,0,0.04)",
        borderRadius: 6,
        padding: "8px 12px",
        marginBottom: 8,
        // Set aside, still legible: an inspector reviewing the decision has to
        // be able to read the form that was removed.
        opacity: excluded ? 0.7 : 1,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <strong style={{ fontSize: 13 }}>
          {doc.document_type === "unknown"
            ? doc.filename
            : `${certLabel(doc.document_type)} — ${doc.filename}`}
          {excluded && (
            <span
              style={{
                background: "#e9ecef",
                color: "#495057",
                borderRadius: 10,
                padding: "1px 7px",
                fontSize: 11,
                fontWeight: 600,
                marginLeft: 8,
              }}
            >
              Set aside
            </span>
          )}
          {blockingCount > 0 && (
            <span
              style={{
                background: "#c92a2a",
                color: "#fff",
                borderRadius: 10,
                padding: "1px 7px",
                fontSize: 11,
                fontWeight: 600,
                marginLeft: 8,
              }}
            >
              {blockingCount} blocking
            </span>
          )}
        </strong>
        {doc.download_url && (
          <button
            type="button"
            onClick={() => {
              setOpenErr(null);
              api.openTraceDocument(doc.id).catch((e: Error) => {
                // Said here rather than swallowed: an inspector who cannot see
                // the original must know that, not click a dead link twice.
                setOpenErr(e instanceof ApiError ? e.detail : e.message);
              });
            }}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
              fontSize: 12,
              color: "#1c7ed6",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Open original ↗
          </button>
        )}
      </div>

      {excluded && (
        <div
          style={{
            background: "#e9ecef",
            border: "1px solid #dee2e6",
            borderRadius: 4,
            padding: "6px 8px",
            marginTop: 6,
            fontSize: 12,
            color: "#495057",
          }}
        >
          Set aside as not belonging to this shipment — the checks do not
          include it.
          {doc.exclusion_reason ? ` Reason: ${doc.exclusion_reason}` : ""}
        </div>
      )}

      {!excluded && doc.extraction_status === "extracted" && (
        <DocumentVerdict findings={mine} implicated={implicated} />
      )}

      {!excluded && doc.extraction_status === "failed" && (
        <p style={{ fontSize: 12, color: "#a61e1e", marginTop: 6 }}>
          This document could not be read automatically
          {doc.extraction_error ? ` (${doc.extraction_error})` : ""}. Check it
          by hand against the order.
        </p>
      )}

      {!excluded && doc.extraction_status === "pending" && (
        <p style={{ ...S.muted, fontSize: 12, marginTop: 6 }}>Not yet read.</p>
      )}

      {doc.extraction_status === "extracted" && f && (
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "4px 12px",
            margin: "8px 0 0",
          }}
        >
          <Row
            label="Part (block 8)"
            value={f.part_number ?? <NotRead />}
            flagged={flagged.has("part_number")}
          />
          <Row
            label="Quantity (block 9)"
            value={f.quantity ?? <NotRead />}
            flagged={flagged.has("quantity")}
          />
          <Row
            label="Serial (block 10)"
            value={f.serial_number ?? <NotRead />}
            flagged={flagged.has("serial_number")}
          />
          <Row
            label="Status (block 11)"
            value={f.status_work ?? <NotRead />}
            flagged={flagged.has("status_work")}
          />
          <Row label="Release" value={<ReleaseBlocks doc={doc} />} />
          <Row
            label="Issued by (block 4)"
            value={f.issuing_organization ?? <NotRead />}
            flagged={flagged.has("issuing_organization")}
          />
          <Row
            label="Signed (block 14e)"
            value={f.signature_date ?? <NotRead />}
            flagged={flagged.has("signature_date")}
          />
          {f.approval_number && (
            <Row label="Approval (block 14c)" value={f.approval_number} />
          )}
          {f.form_tracking_number && (
            <Row
              label="Form tracking (block 3)"
              value={f.form_tracking_number}
            />
          )}
        </dl>
      )}

      {openErr && (
        <p style={{ fontSize: 12, color: "#a61e1e", marginTop: 6 }}>
          Could not open the original: {openErr}
        </p>
      )}

      {onExclusionChange && (
        <div style={{ marginTop: 8 }}>
          <ExclusionControl
            doc={doc}
            canExclude={canExclude}
            busy={busy}
            onChange={(next, reason) => onExclusionChange(doc.id, next, reason)}
          />
        </div>
      )}

      <div style={{ ...S.muted, fontSize: 11, marginTop: 6 }}>
        Arrived via {doc.source_channel}
        {doc.page_count ? ` · ${doc.page_count} page(s)` : ""}
      </div>
    </div>
  );
}
